import { Injectable } from '@angular/core'
import { ApiClient, ApiClientFactory, NotFoundApiError } from '@ftr/api-shared'
import { File } from '@ftr/contracts/api/file'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  IntervalMaxEmissionsReached,
  collectData,
  intervalBackoff,
  mapData,
  mapFailure,
  unwrapData,
} from '@ftr/foundation'
import { Observable, catchError, map, of, switchMap, takeWhile } from 'rxjs'
import { FileUpload, FileUploadError, FileUploadStatusEvent, UploadStates } from '../file-upload'

/**
 * The initial polling interval used to obtain the scanning results
 */
const VIRUS_SCAN_POLLING_INITIAL_INTERVAL = 2_000

/**
 * The maximum polling interval used to obtain the scanning results
 */
const VIRUS_SCAN_POLLING_MAX_INTERVAL = 8_000

/**
 * The maximum number of iterations in the polling
 */
export const VIRUS_SCAN_POLLING_MAX_ITERATIONS = 9

/**
 * A generic service to initialize virus scanning on files uploaded to S3.
 */
@Injectable()
export class VirusScanningService<FileUploadPropertiesType, FileUploadResponseType> {
  private readonly fileApiClient: ApiClient

  constructor(apiClientFactory: ApiClientFactory) {
    this.fileApiClient = apiClientFactory.build('/file')
  }

  /**
   * Calls the `api/file/beginVirusScan` endpoint to initialize the scanning for the given file.
   */
  initializeScan(
    upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    return this.fileApiClient
      .post<FileUploadStatusEvent<FileUploadPropertiesType, FileUploadResponseType>>({
        path: `/${upload.fileId}/beginVirusScan`,
      })
      .pipe(
        mapData(() => {
          upload.status = UploadStates.Scanning
          return upload
        }),
        mapFailure(() => {
          upload.status = UploadStates.Failure
          upload.error = FileUploadError.ScanFailedToStart
          return upload
        }),
        unwrapData(),
      )
  }

  /**
   * Calls `getFileById` for the scanning results. An undefined 'is_infected` means
   * the scanning is still in progress. `IntervalMaxEmissionsReached` will be thrown
   * when the scanning times out.
   */
  checkScanningProgress(
    upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
  ): Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>> {
    return this.getFileById(upload.fileId!).pipe(
      map(file => {
        if (file.isInfected === undefined) {
          upload.status = UploadStates.Scanning
        } else if (file.isInfected) {
          upload.status = UploadStates.Failure
          upload.error = FileUploadError.VirusDetected
        } else {
          upload.status = UploadStates.Success
        }
        return upload
      }),
      catchError(err => {
        upload.status = UploadStates.Failure
        upload.error =
          err instanceof IntervalMaxEmissionsReached
            ? FileUploadError.ScanTimedOut
            : err instanceof NotFoundApiError
              ? FileUploadError.FileUploadFailed
              : FileUploadError.ScanFailed
        return of(upload)
      }),
    )
  }

  /**
   * Polls `GET api/file/:id` until is_infected is true or false. If `is_infected` is still undefined
   * when the number of iterations reaches `VIRUS_SCAN_POLLING_MAX_ITERATIONS`, `IntervalMaxEmissionsReached`
   * will be thrown.
   */
  private getFileById(fileId: Uuid): Observable<File> {
    const config = {
      initialInterval: VIRUS_SCAN_POLLING_INITIAL_INTERVAL,
      maxInterval: VIRUS_SCAN_POLLING_MAX_INTERVAL,
      maxIterations: VIRUS_SCAN_POLLING_MAX_ITERATIONS,
    }

    return intervalBackoff(config).pipe(
      switchMap(() => {
        return this.fileApiClient.get<File>({ path: fileId }).pipe(collectData(file => file))
      }),
      takeWhile(file => file.isInfected === undefined, true),
    )
  }
}
